import React, { useEffect } from 'react';
import { Box, FlexBox, Typography, Button, Logo, Grid, MuiBox } from '@kis-technologies/kis-ui';
import { actions as ticketingActions } from 'services/ticketing';
import { actions as cartActions } from 'services/cart';
import { actions as willCallActions } from 'services/willcall';
import { actions as kioskActions, selectors as kioskSelectors } from 'services/kiosk';
import { selectors as hardwareSelectors } from 'services/hardware';
import { useActions } from 'hooks/useActions';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectors as configSelectors, Configuration, Message } from 'services/configuration';
import PrinterTray from 'assets/printer_tray.png';
import Img from 'react-image';
import { useStyles } from './styled';
import get from 'lodash.get';
import { useTranslation } from 'react-i18next';
import { selectors as mediaSelectors } from 'services/media';

const CompletionView = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const [resetTicketing, resetCart, resetWillCall, checkLowStockThresholds] = useActions([
    ticketingActions.resetTicketing,
    cartActions.resetCart,
    willCallActions.resetWillCall,
    kioskActions.checkLowStockThresholds
  ]);

  const config = useSelector(configSelectors.selectConfiguration) as Configuration;
  const kiosk = useSelector(kioskSelectors.selectKiosk);
  const printData = useSelector(hardwareSelectors.selectPrintData);
  const backgroundImage = useSelector(mediaSelectors.selectBackgroundImage) || [];
  const logoImage = useSelector(mediaSelectors.selectLogoImage) || [];

  const palette = get(config, 'configuration.globalSettings.palette');
  const fonts = get(config, 'configuration.globalSettings.fonts');
  const flowTemplate = get(config, 'configuration.flowTemplate') as string;
  const messaging: Message = get(config, `messaging.${i18n.language}.checkoutCompletion`, {});
  const clientId = get(kiosk, 'venue.clientId');
  const bgImageName = get(backgroundImage[0], 's3Name') || 'default.png';
  const imageBaseUrl = `${process.env.REACT_APP_S3_ASSETS}/${clientId}`;
  const backgroundUrl = `${imageBaseUrl}/background_images/${bgImageName}`;

  const classes = useStyles({ fontFamily: fonts?.primary || '' })();

  useEffect(() => {
    // Fair flow: skip this page and start over if they declined the receipt
    if (flowTemplate === 'fair' && !printData.printJobs.some((job) => job.ticketNumber === 'receipt')) {
      onStartOverClick();

      return () => {};
    }

    const timer = setTimeout(() => {
      onStartOverClick();
    }, 12000);

    return () => clearTimeout(timer);
  }, []);

  const onStartOverClick = () => {
    resetTicketing();
    resetCart();
    resetWillCall();
    checkLowStockThresholds();
    navigate('/ticketing/attract');
  };

  const generateCompletionView = () => {
    if (flowTemplate !== 'fair') {
      return (
        <Grid className={classes.completionOuterContainer}>
          <Grid container className={classes.completionInnerContainer}>
            <FlexBox align="center" justify="center" direction="column">
              <Box flexOverride="none" margin="0 0 8">
                <Typography className={classes.completionHeader}>
                  {(messaging && messaging.header) || t('completion.allSet')}
                </Typography>
              </Box>
              <MuiBox className={classes.completionMessage}>
                <Typography align="center" fontSize={28} sx={{ lineHeight: 1 }}>
                  {messaging && messaging.message}
                </Typography>
              </MuiBox>
              <Box flexOverride="none" width={420}>
                <Button fullWidth onClick={onStartOverClick} customSize="x-large">
                  {(messaging && messaging.buttons && messaging.buttons[0]) || t('commonText.startOver')}
                </Button>
              </Box>
            </FlexBox>
          </Grid>
        </Grid>
      );
    }
    return (
      <FlexBox height="1920px" overflow="hidden">
        <FlexBox sx={{ width: '1080px', backgroundImage: `url(${backgroundUrl})` }}>
          <FlexBox height="12.5%" fullWidth align="end" justify="center">
            {logoImage && logoImage.length > 0 ? (
              <img alt="url" src={`${imageBaseUrl}/logo_images/${logoImage[0].s3Name}`} style={{ height: 97 }} />
            ) : (
              <Logo />
            )}
          </FlexBox>
          <FlexBox height="12.7%" fullWidth align="end" justify="center">
            <Typography
              color={palette?.primaryText}
              sx={{ lineHeight: '0.96', fontFamily: fonts?.secondary }}
              fontSize={125}
              align="center"
            >
              THANK YOU FOR
              <br />
              YOUR PURCHASE
            </Typography>
          </FlexBox>

          <FlexBox height="5%" fullWidth align="center" justify="center">
            <FlexBox fullWidth align="center" justify="center">
              <Typography
                color={palette?.primaryText}
                fontSize={42}
                sx={{ fontFamily: fonts?.primary, margin: '5px' }}
                align="center"
              >
                DON&apos;T FORGET TO TAKE
                <br />
                YOUR RECEIPT.
              </Typography>
            </FlexBox>
          </FlexBox>
          <FlexBox height="40%" fullWidth align="center" justify="center">
            <Img src={PrinterTray} width={326} height={384} />
            <Box flexOverride="none" width={790} height={200}>
              {!printData.loading && (
                <Button
                  fullWidth
                  onClick={onStartOverClick}
                  customSize="x-large"
                  sx={{
                    backgroundColor: palette?.primary,
                    borderColor: palette?.primary,
                    color: palette?.secondaryText,
                    fontFamily: fonts?.primary
                  }}
                >
                  <Typography fontSize={40} align="center">
                    {(messaging && messaging.buttons && messaging.buttons[0]) || 'START OVER'}
                  </Typography>
                </Button>
              )}
            </Box>
          </FlexBox>
        </FlexBox>
      </FlexBox>
    );
  };

  return <>{generateCompletionView()}</>;
};

export default CompletionView;
